/**
 * <TutorApplicationJourney> — le parcours de la candidature, où l'on se situe d'un coup d'œil (organism).
 *
 * Un seul chemin, de haut en bas, où chaque étape se lit une fois. Chaque étape est un jalon au bout d'un trait : la tête du
 * champion qui la mène (celui du profil, de la motivation, de la pédagogie, de la technique), avec sa
 * pastille d'état (coche verte, ou son numéro).
 * Le trait est vert jusqu'à l'étape du moment, puis en pointillé. L'étape du moment s'ouvre EN PLACE,
 * dans le chemin : la seule carte encadrée de la page (`<HighlightFrame>`), « Tu es ici », son champion
 * en pied qui dit ce qui reste dans sa bulle, et le bouton. Les étapes à venir ont la tête estompée.
 *
 * En tête, « Étape 3 sur 6 » et la barre segmentée du livre (`<SegmentedBar gap>`), une pastille par
 * étape. Le squelette, `<TutorApplicationJourneySkeleton>`, prend la géométrie de ce fichier.
 */
import React from "react"
import { View } from "react-native"
import Svg, { Line } from "react-native-svg"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { type UITutorApplicationStep } from "../../../ports/primary/ui/types/tutorCandidature"
import { ChampionAvatar } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { ChampionFigure } from "../../atoms/ChampionFigure/ChampionFigure"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { ProgressBar } from "../../atoms/ProgressBar"
import { SegmentedBar, STEP_BAR_HEIGHT } from "../../atoms/SegmentedBar"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { SPEECH_BUBBLE_LIFT, SpeechBubble } from "../../molecules/SpeechBubble"
import { useWidthAtLeast } from "../../hooks/useWidthAtLeast"

const JOURNEY_TEXTS = {
    position: (current: number, total: number) => `Étape ${current} sur ${total}`,
    done: (count: number, total: number) => `${count} / ${total} faites`,
    here: "Tu es ici",
    step: (step: number) => `Étape ${step}`,
    doneLabel: "Terminée",
    /** Ce que le lecteur d'écran dit d'un jalon : l'état que la couleur, l'estompe et le trait montrent. */
    state: { done: "terminée", current: "en cours", locked: "pas encore débloquée" },
    stop: (step: number, state: string, title: string, kind?: string) =>
        [`Étape ${step}, ${state}`, title, kind].filter(Boolean).join(". "),
    video: "En vidéo",
    form: "Questionnaire",
    start: "Commencer",
    resume: "Continuer",
    percent: (percent: number) => `${percent} % de l'étape fait`,
    remaining: (count: number) =>
        count > 1
            ? `Il reste ${count} étapes pour finir ta candidature !`
            : "Plus qu'une étape pour finir ta candidature !",
}

/** La tête d'un jalon : assez grande pour reconnaître le champion, alignée sur le trait. */
const NODE_SIZE = 44
/** L'anneau de l'étape du moment autour de la tête : son air, puis son trait. */
const NODE_RING = 3
/** Le jalon entier, anneau compris : la largeur de la gouttière du chemin (et du squelette). */
export const JOURNEY_NODE_BOX = NODE_SIZE + 4 * NODE_RING
/** Le champion de l'étape du moment, en pied dans sa carte : petit au téléphone, plus grand au large. */
export const JOURNEY_FIGURE_NARROW = 132
const FIGURE_WIDE = 188
/** Au-delà, le champion et sa bulle se posent côte à côte avec le bouton. */
const WIDE_CARD = 560

export type TutorApplicationJourneyProps = {
    steps: UITutorApplicationStep[]
    onStart: () => void
    /** Le compte se relit : le bouton tourne. */
    loading?: boolean
    testID?: string
}

export function TutorApplicationJourney(props: TutorApplicationJourneyProps) {
    const { steps, onStart, loading = false, testID } = props
    const current = steps.find((step) => step.status === "current")
    const doneCount = steps.filter((step) => step.status === "done").length
    const remaining = steps.length - doneCount

    return (
        <Stack gap="5" testID={testID}>
            <Stack gap="2">
                <Row justify="between" align="end">
                    <Text size="text-lg" weight="bold" color="text-primary" accessibilityRole="header">
                        {JOURNEY_TEXTS.position(current?.step ?? steps.length, steps.length)}
                    </Text>
                    <Text size="text-sm" color="text-muted">
                        {JOURNEY_TEXTS.done(doneCount, steps.length)}
                    </Text>
                </Row>
                <SegmentedBar
                    gap="1"
                    height={STEP_BAR_HEIGHT}
                    max={steps.length}
                    segments={steps.map((step) => ({ value: 1, color: STEP_COLOR[step.status] }))}
                />
            </Stack>

            <View>
                {steps.map((step, index) => (
                    <JourneyStop
                        key={step.step}
                        step={step}
                        first={index === 0}
                        last={index === steps.length - 1}
                        remaining={remaining}
                        onStart={onStart}
                        loading={loading}
                    />
                ))}
            </View>
        </Stack>
    )
}

type JourneyStopProps = {
    step: UITutorApplicationStep
    first: boolean
    last: boolean
    remaining: number
    onStart: () => void
    loading: boolean
}

function JourneyStop(props: JourneyStopProps) {
    const { step, first, last, remaining, onStart, loading } = props
    const isCurrent = step.status === "current"
    const locked = step.status === "locked"
    // Le trait qui arrive est vert si l'étape d'avant est faite (donc jusqu'à l'étape du moment) ;
    // celui qui part l'est si celle-ci est faite.
    const inDone = step.status !== "locked"
    const outDone = step.status === "done"
    const kind = formOf(step)

    return (
        <View
            style={styles.stop}
            // Un jalon se lit d'un trait ; l'étape du moment garde ses éléments, son bouton en tête.
            accessible={!isCurrent}
            accessibilityLabel={
                isCurrent
                    ? undefined
                    : JOURNEY_TEXTS.stop(
                          step.step,
                          JOURNEY_TEXTS.state[step.status],
                          step.title,
                          // Une étape faite a déjà dit son état.
                          step.status === "done" ? undefined : kind
                      )
            }
        >
            <View style={styles.gutter}>
                <JourneyLine done={inDone} style={[styles.lineIn(isCurrent), first && styles.hidden]} />
                <JourneyNode step={step} />
                <JourneyLine done={outDone} style={[styles.lineOut, last && styles.hidden]} />
            </View>
            <View style={styles.body(isCurrent, last)}>
                {isCurrent ? (
                    <CurrentStepCard step={step} remaining={remaining} onStart={onStart} loading={loading} />
                ) : (
                    <Stack gap="1" style={styles.compact}>
                        <Text
                            size="text-md"
                            weight="semibold"
                            color={locked ? "text-muted" : "text-primary"}
                            numberOfLines={1}
                        >
                            {step.title}
                        </Text>
                        <Text
                            size="text-sm"
                            weight={step.status === "done" ? "semibold" : "regular"}
                            color={step.status === "done" ? "text-success" : "text-muted"}
                        >
                            {step.status === "done" ? JOURNEY_TEXTS.doneLabel : kind}
                        </Text>
                    </Stack>
                )}
            </View>
        </View>
    )
}

/**
 * Un bout du trait du chemin : plein et vert jusqu'à l'étape du moment, en pointillé après. Le pointillé
 * est un trait SVG : React Native iOS ne dessine pas un bord en pointillé sur un seul côté (il le
 * dessine plein).
 */
function JourneyLine(props: { done: boolean; style: React.ComponentProps<typeof View>["style"] }) {
    const { theme } = useUnistyles()
    return (
        <View style={[styles.line(props.done), props.style]}>
            {!props.done && (
                // Posé par-dessus : la hauteur du trait est celle que lui donne le jalon, jamais celle du SVG
                // (une feuille Unistyles ne s'applique pas au SVG, d'où la vue qui le porte).
                <View style={styles.dash}>
                    <Svg width={LINE_WIDTH} height="100%">
                        <Line
                            x1={LINE_WIDTH / 2}
                            y1="0"
                            x2={LINE_WIDTH / 2}
                            y2="100%"
                            stroke={theme.colors.border}
                            strokeWidth={LINE_WIDTH}
                            strokeDasharray={LINE_DASH}
                        />
                    </Svg>
                </View>
            )}
        </View>
    )
}

/** La tête du champion de l'étape, et sa pastille : la coche, ou le numéro. */
function JourneyNode(props: { step: UITutorApplicationStep }) {
    const { step } = props
    const done = step.status === "done"
    const isCurrent = step.status === "current"
    return (
        <View style={styles.node(isCurrent)}>
            <ChampionAvatar name={step.champion} size={NODE_SIZE} dimmed={step.status === "locked"} />
            <View style={styles.marker(step.status)}>
                {done ? (
                    <Icon name="check" size={10} color="white" />
                ) : (
                    <Text size="text-xs" weight="bold" color={isCurrent ? "text-on-role" : "text-muted"}>
                        {step.step}
                    </Text>
                )}
            </View>
        </View>
    )
}

type CurrentStepCardProps = {
    step: UITutorApplicationStep
    remaining: number
    onStart: () => void
    loading: boolean
}

/** L'étape du moment, ouverte dans le chemin. */
function CurrentStepCard(props: CurrentStepCardProps) {
    const { step, remaining, onStart, loading } = props
    const { onLayout, atLeast: wide } = useWidthAtLeast(WIDE_CARD)
    const completion = Math.round(step.completion ?? 0)
    const started = completion > 0
    const progress = started ? <Progress completion={completion} /> : null
    const action = (
        <Button
            label={started ? JOURNEY_TEXTS.resume : JOURNEY_TEXTS.start}
            fullWidth
            loading={loading}
            onPress={onStart}
        />
    )

    return (
        <HighlightFrame>
            <Card p="5">
                <View onLayout={onLayout}>
                    <Stack gap="4">
                        <Row gap="2">
                            <View style={styles.here}>
                                <Text size="text-xs" weight="bold" color="text-on-role">
                                    {JOURNEY_TEXTS.here}
                                </Text>
                            </View>
                            <Text size="text-sm" weight="semibold" color="text-muted">
                                {JOURNEY_TEXTS.step(step.step)} · {formOf(step)}
                            </Text>
                        </Row>
                        <Stack gap="1">
                            <Text size="text-xl" weight="bold" color="text-primary" accessibilityRole="header">
                                {step.title}
                            </Text>
                            <Text size="text-base" color="text-primary">
                                {step.description}
                            </Text>
                        </Stack>

                        {/* Un seul arbre : au seuil, le champion change de côté sans être remonté. */}
                        <View style={styles.scene(wide)}>
                            <ChampionFigure
                                name={step.champion}
                                height={wide ? FIGURE_WIDE : JOURNEY_FIGURE_NARROW}
                                halo
                            />
                            <Stack gap="4" style={styles.sceneSide(wide)}>
                                <SpeechBubble tail={wide ? "left" : "right"}>
                                    {JOURNEY_TEXTS.remaining(remaining)}
                                </SpeechBubble>
                                {wide && progress}
                                {wide && action}
                            </Stack>
                        </View>
                        {!wide && progress}
                        {!wide && action}
                    </Stack>
                </View>
            </Card>
        </HighlightFrame>
    )
}

function Progress(props: { completion: number }) {
    return (
        <Stack gap="1">
            <ProgressBar value={props.completion} color="role" />
            <Text size="text-sm" color="text-muted">
                {JOURNEY_TEXTS.percent(props.completion)}
            </Text>
        </Stack>
    )
}

/** La question ou la vidéo d'une étape, dite sur son jalon et dans sa carte. */
function formOf(step: UITutorApplicationStep): string {
    return step.video ? JOURNEY_TEXTS.video : JOURNEY_TEXTS.form
}

const STEP_COLOR = { done: "success", current: "role", locked: "border" } as const

const MARKER = 20
const LINE_WIDTH = 3
const LINE_DASH = "5 4"

const styles = StyleSheet.create((theme) => ({
    stop: { flexDirection: "row", alignItems: "stretch" },
    gutter: { width: JOURNEY_NODE_BOX, alignItems: "center" },
    line: (done: boolean) => ({
        width: LINE_WIDTH,
        backgroundColor: done ? theme.colors.success : undefined,
    }),
    lineIn: (isCurrent: boolean) => ({ height: isCurrent ? 22 : 10 }),
    lineOut: { flex: 1, minHeight: 10 },
    dash: { position: "absolute", top: 0, bottom: 0, left: 0, width: LINE_WIDTH },
    hidden: { opacity: 0 },
    node: (isCurrent: boolean) => ({
        borderRadius: theme.radius.full,
        padding: NODE_RING,
        borderWidth: NODE_RING,
        borderColor: isCurrent ? theme.colors.role : "transparent",
    }),
    marker: (status: UITutorApplicationStep["status"]) => ({
        position: "absolute",
        right: -4,
        bottom: -4,
        width: MARKER,
        height: MARKER,
        borderRadius: MARKER / 2,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 2,
        borderColor: theme.colors.surface,
        backgroundColor:
            status === "done"
                ? theme.colors.success
                : status === "current"
                  ? theme.colors["role-fill"]
                  : theme.colors["surface-subtle"],
    }),
    body: (isCurrent: boolean, last: boolean) => ({
        flex: 1,
        minWidth: 0,
        paddingLeft: theme.spacing["3"],
        paddingTop: isCurrent ? 0 : theme.spacing["2"],
        paddingBottom: last ? 0 : isCurrent ? theme.spacing["5"] : theme.spacing["4"],
    }),
    compact: { paddingTop: 2 },
    here: {
        backgroundColor: theme.colors["role-fill"],
        borderRadius: theme.radius.full,
        paddingHorizontal: theme.spacing["2"],
        paddingVertical: 2,
    },
    // Au téléphone, la bulle à gauche du champion (second enfant, la rangée est inversée).
    scene: (wide: boolean) => ({
        flexDirection: wide ? "row" : "row-reverse",
        alignItems: "flex-end",
        gap: wide ? theme.spacing["3"] : theme.spacing["2"],
    }),
    sceneSide: (wide: boolean) => ({
        flex: 1,
        minWidth: 0,
        paddingBottom: wide ? theme.spacing["2"] : theme.spacing[SPEECH_BUBBLE_LIFT],
    }),
}))
